import {
  boundsToMapboxCoordinates,
  validateBounds,
} from "./ideamBartBoundsUtils.js";
import {
  IDEAM_BART_CALIBRATION_CONFIG,
  IDEAM_BART_OVERLAY_CONFIG,
  resolveIdeamBartColombiaBounds,
} from "../constants/ideamBartRadarOverlay.js";

const STORAGE_KEY = "midpoint_radar_calibration_v1";
const CHANGE_EVENT = "midpoint:radar-calibration-change";

function readStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStorage(state) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state }));
}

function createDefaultState() {
  const configuredBounds = resolveIdeamBartColombiaBounds();
  return {
    active: false,
    previewBounds: configuredBounds,
    configuredBounds,
    opacity: IDEAM_BART_OVERLAY_CONFIG.defaultOpacity,
    backupOpacity: 0.5,
    showBoundsOutline: false,
    showReferenceMarkers: false,
    comparisonMode: "official",
    nudgeStep: 0.05,
    baseMapDimmed: false,
    checklist: {},
    calibrationVersion: IDEAM_BART_CALIBRATION_CONFIG.calibrationVersion,
    notes: IDEAM_BART_CALIBRATION_CONFIG.notes,
  };
}

export function getRadarCalibrationState() {
  const stored = readStorage();
  const defaults = createDefaultState();

  if (!stored) {
    return defaults;
  }

  return {
    ...defaults,
    ...stored,
    configuredBounds: defaults.configuredBounds,
    previewBounds: stored.previewBounds || defaults.previewBounds,
  };
}

export function setRadarCalibrationState(patch = {}) {
  const current = getRadarCalibrationState();
  const next = { ...current, ...patch };

  if (patch.previewBounds) {
    const validation = validateBounds(patch.previewBounds);
    if (!validation.valid) {
      return { ok: false, error: validation.message, state: current };
    }
  }

  writeStorage(next);
  return { ok: true, state: next };
}

export function resetPreviewBounds() {
  const configuredBounds = resolveIdeamBartColombiaBounds();
  return setRadarCalibrationState({ previewBounds: configuredBounds });
}

export function activateRadarCalibration(patch = {}) {
  return setRadarCalibrationState({
    active: true,
    showBoundsOutline: true,
    ...patch,
  });
}

export function deactivateRadarCalibration() {
  return setRadarCalibrationState({ active: false });
}

export function isRadarCalibrationActive() {
  return Boolean(getRadarCalibrationState().active);
}

export function resolveEffectiveCalibrationBounds(state = getRadarCalibrationState()) {
  const bounds = state.previewBounds || state.configuredBounds;
  const validation = validateBounds(bounds);
  if (!validation.valid) {
    return state.configuredBounds;
  }
  return bounds;
}

export function buildCalibrationDiagnostics(state = getRadarCalibrationState()) {
  const bounds = resolveEffectiveCalibrationBounds(state);
  const mapboxCoordinates = boundsToMapboxCoordinates(bounds);

  return {
    source: IDEAM_BART_CALIBRATION_CONFIG.source,
    sourceLabel: IDEAM_BART_CALIBRATION_CONFIG.sourceLabel,
    bounds,
    mapboxCoordinates,
    calibrationVersion: state.calibrationVersion,
    notes: state.notes,
    opacity: state.opacity,
    backupOpacity: state.backupOpacity,
    showBoundsOutline: state.showBoundsOutline,
    showReferenceMarkers: state.showReferenceMarkers,
    comparisonMode: state.comparisonMode,
    active: state.active,
    generatedAt: new Date().toISOString(),
  };
}

export function subscribeRadarCalibration(listener) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handler = (event) => {
    listener(event.detail || getRadarCalibrationState());
  };

  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export function parseCalibrationUrlFlag(search = "") {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    return params.get("radarCalibration") === "1";
  }

  return String(search).includes("radarCalibration=1");
}
